import type { DataProvider } from "react-admin";
import { API_BASE, TOKEN_KEY } from "./config";
import { handleSessionExpired, SessionExpiredError, tryRefresh } from "./authProvider";

const failSession = (): never => {
  handleSessionExpired();
  throw new SessionExpiredError();
};

const buildHeaders = (extra: Record<string, string> = {}): HeadersInit => {
  const h: Record<string, string> = { "Content-Type": "application/json", ...extra };
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

const sendOnce = async (url: string, options: RequestInit) =>
  fetch(`${API_BASE}${url}`, {
    ...options,
    headers: buildHeaders(options.headers as Record<string, string>),
  });

const httpJson = async (url: string, options: RequestInit = {}) => {
  let res = await sendOnce(url, options);

  if (res.status === 401) {
    const newToken = await tryRefresh();
    if (newToken) {
      res = await sendOnce(url, options);
    } else {
      failSession();
    }
  }

  if (!res.ok) {
    if (res.status === 401) failSession();
    let body: { message?: string } = {};
    try {
      body = await res.json();
    } catch {
      body = { message: res.statusText };
    }
    const err = new Error(body?.message ?? `HTTP ${res.status}`) as Error & {
      status: number;
      body: unknown;
    };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
};

const buildListBody = (filter: Record<string, unknown> | undefined): Record<string, unknown> => {
  const body: Record<string, unknown> = {};
  if (!filter) return body;
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null || value === "") continue;
    body[key] = value;
  }
  return body;
};

const toPascalKey = (k: string): string =>
  k.length === 0 ? k : k.charAt(0).toUpperCase() + k.slice(1);

const KEEP_LOWER = new Set(["id"]);

const pascalize = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(pascalize);
  if (value && typeof value === "object" && value.constructor === Object) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      const pv = pascalize(v);
      const pk = toPascalKey(k);
      if (KEEP_LOWER.has(k)) {
        out[k] = pv;
        continue;
      }
      out[pk] = pv;
      if (pk !== k) out[k] = pv;
    }
    return out;
  }
  return value;
};

const normalize = <T extends Record<string, unknown>>(row: T): T & { id: unknown } => {
  if (row == null) return row as T & { id: unknown };
  const pascal = pascalize(row) as T & { id?: unknown; Id?: unknown };
  const idVal = pascal.id ?? pascal.Id;
  if (idVal != null) {
    pascal.id = idVal;
    pascal.Id = idVal;
  }
  return pascal as T & { id: unknown };
};

const denormalize = (row: Record<string, unknown>) => {
  const { id, ...rest } = row;
  if (rest.Id == null && id != null) rest.Id = id;
  // pascalize() keeps camelCase twins for display, but on write they are stale
  // copies — the backend binds case-insensitively and the later key wins.
  for (const k of Object.keys(rest)) {
    const pk = toPascalKey(k);
    if (pk !== k && pk in rest) delete rest[k];
  }
  return rest;
};

export const dataProvider: DataProvider = {
  async getList(resource, { pagination, sort, filter }) {
    const limit = pagination?.perPage ?? 25;
    const offset = ((pagination?.page ?? 1) - 1) * limit;
    const sortColumn = sort?.field ? toPascalKey(sort.field) : "Id";
    const sortDirection = (sort?.order ?? "DESC").toUpperCase();

    const body = {
      ...buildListBody(filter),
      limit,
      offset,
      sortColumn,
      sortDirection,
    };

    const res = await httpJson(`/api/${resource}/list`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    const items = (res?.Items ?? res?.items ?? []) as Record<string, unknown>[];
    const total = Number(res?.Total ?? res?.total ?? items.length);
    return { data: items.map(normalize) as never[], total };
  },

  async getOne(resource, { id }) {
    const data = await httpJson(`/api/${resource}/${id}`);
    return { data: normalize(data) as never };
  },

  async getMany(resource, { ids }) {
    const res = await httpJson(`/api/${resource}/list`, {
      method: "POST",
      body: JSON.stringify({ ids, limit: 1000, offset: 0 }),
    });
    const items = (res?.Items ?? res?.items ?? []) as Record<string, unknown>[];
    return { data: items.map(normalize) as never[] };
  },

  async getManyReference(resource, { target, id, pagination, sort, filter }) {
    const merged = { ...(filter ?? {}), [target]: id };
    return this.getList(resource, { pagination, sort, filter: merged, meta: undefined });
  },

  async create(resource, { data }) {
    const created = await httpJson(`/api/${resource}/create`, {
      method: "POST",
      body: JSON.stringify(denormalize(data as Record<string, unknown>)),
    });
    return { data: normalize(created) };
  },

  async update(resource, { id, data, previousData }) {
    const prev = previousData ? denormalize(previousData as Record<string, unknown>) : {};
    const patch = denormalize(data as Record<string, unknown>);
    const merged = { ...prev, ...patch, Id: id };
    const updated = await httpJson(`/api/${resource}/update`, {
      method: "POST",
      body: JSON.stringify(merged),
    });
    return { data: normalize(updated ?? merged) };
  },

  async updateMany(resource, { ids, data }) {
    const patch = denormalize(data as Record<string, unknown>);
    await Promise.all(
      ids.map((id) =>
        httpJson(`/api/${resource}/update`, {
          method: "POST",
          body: JSON.stringify({ ...patch, Id: id }),
        }),
      ),
    );
    return { data: ids };
  },

  async delete(resource, { id }) {
    await httpJson(`/api/${resource}/markDeleted`, {
      method: "POST",
      body: JSON.stringify({ Id: id }),
    });
    return { data: { id } as never };
  },

  async deleteMany(resource, { ids }) {
    await Promise.all(
      ids.map((id) =>
        httpJson(`/api/${resource}/markDeleted`, {
          method: "POST",
          body: JSON.stringify({ Id: id }),
        }),
      ),
    );
    return { data: ids };
  },
};

export const apiFetch = httpJson;

export const authUpload = async (file: File): Promise<string> => {
  const sendUpload = async () => {
    const fd = new FormData();
    fd.append("file", file);
    const token = localStorage.getItem(TOKEN_KEY);
    return fetch(`${API_BASE}/api/file/upload`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
  };

  let res = await sendUpload();
  if (res.status === 401) {
    const newToken = await tryRefresh();
    if (newToken) {
      res = await sendUpload();
    } else {
      failSession();
    }
  }

  if (!res.ok) {
    if (res.status === 401) failSession();
    let body: { message?: string } = {};
    try {
      body = await res.json();
    } catch {
      body = { message: res.statusText };
    }
    throw new Error(body?.message ?? `HTTP ${res.status}`);
  }

  const data = await res.json();
  const uid = data?.id ?? data?.Id;
  if (!uid) throw new Error("Upload response did not contain an id");
  return String(uid);
};
